import { DocContent } from '../docsData';

// {{CONNECTOR_URL}} / {{ACCOUNT_ID}} / {{ACCOUNT_NAME}} are filled in by
// DocPage for readers who are signed in. This page is public, so everything
// still has to read sensibly with the placeholders' fallbacks in place.
const doc: DocContent = {
  title: 'MCP Server',
  content: `
Pliable exposes your semantic layer over the **Model Context Protocol (MCP)** — an open standard that lets AI assistants use external tools and data.

Connect it, and Claude (or another MCP-capable assistant) can answer questions from your actual metrics instead of guessing: it looks up what exists, runs analyses against your warehouse, and reads and writes your knowledge base.

---

## Your connector URL

\`\`\`
{{CONNECTOR_URL}}
\`\`\`

That's all anyone needs to add Pliable to their assistant. If you're signed in, the URL above is already yours — the last part is your **Account ID**, which you'll also find in Settings.

---

## How access works

Adding the connector doesn't grant anyone anything on its own. **Each person signs in with their own Pliable account**, and sees exactly what they'd see in the app:

- Metrics hidden from them stay hidden — the assistant never learns they exist
- Row-level restrictions apply to every query
- What they can do depends on their role

| Role | Can |
|---|---|
| Admin, Builder | Look up metrics, run analyses, read and write the knowledge base |
| ReadOnly | Look up metrics, run analyses, read the knowledge base |

Nobody can reach raw SQL or change your data model through MCP. Those tools aren't exposed.

You can see who has connected — and disconnect them — under **Settings → MCP**.

---

## Claude (Team or Enterprise)

An owner adds the connector once, then each person connects individually.

**Owner, once:**

{{INSTALL_BUTTON}}

That opens Claude's organization connector settings with the name and URL already filled in. You review the values and confirm — nothing is added until you do, and it grants no access on its own.

Prefer to do it by hand?

1. **Organization settings → Connectors**
2. **Add → Custom**. If asked for a type, choose **Web**
3. Paste \`{{CONNECTOR_URL}}\`
4. Leave the **OAuth Client ID and Secret empty** — Claude identifies itself automatically
5. **Add**

**Then everyone:**

1. **Customize → Connectors**
2. Find Pliable and select **Connect**
3. Sign in and approve

---

## Claude (Free, Pro, or Max)

1. **Customize → Connectors**
2. **Add custom connector**
3. Paste \`{{CONNECTOR_URL}}\`, leave the OAuth fields empty, and select **Add**
4. Select **Connect** and sign in

---

## Claude Code

\`\`\`bash
claude mcp add --transport http pliable {{CONNECTOR_URL}}
\`\`\`

Then run \`/mcp\` and choose **Authenticate**. A browser opens for you to sign in and approve.

---

## ChatGPT

Custom MCP connectors in ChatGPT are in beta, on the web, for Plus, Pro, Business, Enterprise and Education plans.

**On Business or Enterprise**, an admin enables it first:

1. **Workspace Settings → Permissions & Roles → Connected Data**
2. Allow **Create custom MCP connectors**

**Then, for each person:**

1. **Settings → Apps → Advanced settings** and turn on **Developer mode**
2. **Settings → Connectors → Create**
3. Give it a name (\`Pliable\`) and a description — ChatGPT reads the description when deciding whether to use it, so something like *"Query our metrics and knowledge base"* works well
4. Paste \`{{CONNECTOR_URL}}\` as the MCP server URL
5. Choose **OAuth** for authentication, then sign in and approve

ChatGPT supports the same client registration method Pliable uses, so there is nothing to configure beyond the URL.

---

## Other MCP clients

Any client that supports **remote MCP servers over HTTP with OAuth** can connect. Give it \`{{CONNECTOR_URL}}\` and it discovers everything else — where to sign in, what permissions to request — on its own.

Two things worth knowing before you try one:

**Your client must handle OAuth sign-in.** Pliable issues a credential to each person after they sign in; there is no static API key to paste, because a shared key couldn't tell your users apart or apply their individual permissions.

**Some clients only support an older registration method.** MCP is moving from Dynamic Client Registration to Client ID Metadata Documents, and Pliable implements the newer one — as do Claude and ChatGPT. A client that only supports the older method won't connect yet. If yours fails at the sign-in step, that's the likely reason; [tell us](mailto:support@pliable.co) which client and we'll take a look.

> **Using the OpenAI API directly?** The Responses API's MCP tool expects you to hand it a token rather than running a browser sign-in, so there's no way to obtain a Pliable credential from within it. Connect through ChatGPT instead, or [get in touch](mailto:support@pliable.co) if you need a programmatic path — the [Ellie Invoke API](/docs/ellie-invoke-api) may already do what you want.

---

## What the assistant can do

| Tool | What it does |
|---|---|
| \`list_metrics_and_dimensions\` | Lists every metric and dimension the signed-in person can query |
| \`similar_column_values\` | Finds how a value is actually spelled in your data before filtering on it |
| \`generate_and_run_analysis\` | Builds and runs an analysis, returning the data |
| \`search_kb\`, \`get_kb_article\`, \`list_kb_folder\` | Reads your knowledge base |
| \`create_kb_article\`, \`update_kb_article\`, \`create_kb_folder\` | Writes to your knowledge base |

A good first question once you're connected: **"What metrics do we have?"**

---

## Troubleshooting

**"Couldn't reach the MCP server."** Usually a typo in the URL. Check it matches \`{{CONNECTOR_URL}}\` exactly, including the account at the end.

**Sign-in opens but fails.** Confirm you have access to {{ACCOUNT_NAME}} in Pliable — you can only connect to accounts you already belong to.

**The assistant says a metric doesn't exist.** Ask it to list the metrics first. Names have to match exactly, and anything restricted from your account won't appear.

**One analysis at a time.** Analyses build a table in your warehouse, so Pliable runs one per connection at a time. If an assistant asks for several at once it'll be told to combine them — usually one broader question answers what several narrow ones would.

**Connecting more than one account.** Each Pliable account has its own connector URL. Add each one separately; they'll appear as separate connectors.
`,
};

export default doc;
